import { useLazyQuery, useMutation } from '@apollo/client/react';
import { useAtom, useSetAtom } from 'jotai';
import React, { useCallback, useEffect } from 'react';

import { Alert, Button, EntryList, Icon, Modal, Name } from '~/components/blocks';
import {
  DeactivatePractitionerDocument,
  GetMessagePharmacistDocument,
  GetSettingsPractitionerDocument,
  GetSettingsPractitionerQuery,
  GetSettingsPractitionersDocument,
  SettingsPractitionerFragment,
} from '~/graphql/generated/graphql';
import {
  deleteModalState,
  editDrawerState,
  pageInfoState,
} from '~/state/settings_practitioners/atoms';
import { getPageNumberAfterDelete } from '~/utils/pagenation';

const formatPractitioner = (data: GetSettingsPractitionerQuery) => {
  return data.node ? (data.node as SettingsPractitionerFragment) : null;
};

export const DeletePractitionerModal = () => {
  const [state, setModalState] = useAtom(deleteModalState);
  const setEditDrawerState = useSetAtom(editDrawerState);
  const [pageInfo, setPageInfo] = useAtom(pageInfoState);

  const [getPractitioner, { data }] = useLazyQuery(GetSettingsPractitionerDocument);
  const [deactivePractitioner, { data: deactiveData, loading, error }] = useMutation(
    DeactivatePractitionerDocument,
    {
      refetchQueries: [{ query: GetMessagePharmacistDocument }],
      errorPolicy: 'all',
    },
  );

  const practitioner = data ? formatPractitioner(data) : null;

  useEffect(() => {
    if (!deactiveData?.deactivatePractitioner) return;
    setModalState({ isOpen: false, practitionerId: null, error: null });
    setEditDrawerState((_state) => ({ ..._state, isOpen: false, practitionerId: null }));
  }, [deactiveData, setEditDrawerState, setModalState]);

  const handleClose = useCallback(
    () => setModalState({ isOpen: false, practitionerId: null, error: null }),
    [setModalState],
  );
  const handleSubmit = useCallback(async () => {
    if (state.practitionerId) {
      const page = getPageNumberAfterDelete(pageInfo.page, pageInfo.perPage, pageInfo.totalCount);
      const refetchQueries = [
        {
          query: GetSettingsPractitionersDocument,
          variables: { page, perPage: pageInfo.perPage },
        },
      ];
      await deactivePractitioner({
        variables: {
          input: {
            practitionerId: state.practitionerId,
          },
        },
        refetchQueries,
      });

      if (page != pageInfo.page) {
        setPageInfo((_pageInfo) => ({ ..._pageInfo, page }));
      }
    }
  }, [
    deactivePractitioner,
    pageInfo.page,
    pageInfo.perPage,
    pageInfo.totalCount,
    setPageInfo,
    state.practitionerId,
  ]);

  useEffect(() => {
    if (state.isOpen && state.practitionerId) {
      getPractitioner({
        variables: {
          id: state.practitionerId,
        },
      });
    }
  }, [getPractitioner, state.isOpen, state.practitionerId]);

  useEffect(() => {
    if (error) {
      setModalState((state) => {
        return { ...state, error: error.message };
      });
    }
  }, [error, setModalState]);

  if (!practitioner) return null;

  return (
    <Modal open={state.isOpen} size="s" onClose={handleClose}>
      <Modal.Header>スタッフ削除</Modal.Header>
      <Modal.Body>
        {state.error && <Alert status="error"> {state.error}</Alert>}
        <EntryList>
          <EntryList.Head>ユーザID</EntryList.Head>
          <EntryList.Body>{practitioner.authentication.username}</EntryList.Body>
        </EntryList>
        <EntryList>
          <EntryList.Head>スタッフ名</EntryList.Head>
          <EntryList.Body>
            <Name
              familyName={practitioner.familyName}
              givenName={practitioner.givenName}
              phoneticFamilyName={practitioner.phoneticFamilyName}
              phoneticGivenName={practitioner.phoneticGivenName}
            />
          </EntryList.Body>
        </EntryList>
      </Modal.Body>
      <Modal.Footer>
        <Button use="secondary" loading={loading} onClick={handleSubmit}>
          <Icon icon="trash" size="m" color="white" />
          削除
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
